import { useCallback } from 'react';

import { TaskProcessorProviderWrapper } from '@eluve/appointment-tasks';
import { FCC } from '@eluve/components';
import {
  fileSyncTriggerStore,
  getAppointmentManifest,
  useDb,
} from '@eluve/user-local-files';

/**
 * Bridges the local Dexie manifest into the task processor without the
 * `@eluve/appointment-tasks` lib taking a circular dependency on
 * `@eluve/user-local-files`. [ENG-4717]
 */
export const TaskProcessorProviderBridge: FCC = ({ children }) => {
  const db = useDb();

  // Stable identities so the wrapper's useMemo doesn't rebuild the TaskProcessor
  // (and drop in-flight tasks) on every parent rerender.
  const loadManifestFiles = useCallback(
    async (appointmentId: string) => {
      const manifest = await getAppointmentManifest(db, appointmentId);
      return (
        manifest?.files.map((f) => ({
          fileName: f.fileName,
          byteSize: f.byteSize,
          packetCount: f.packetCount,
          recordingStartedAt: f.recordingStartedAt,
          firstPacketAt: f.firstPacketAt,
          lastPacketAt: f.lastPacketAt,
          wasDegraded: f.wasDegraded,
        })) ?? []
      );
    },
    [db],
  );

  const requestFileSync = useCallback(
    () => fileSyncTriggerStore.send({ type: 'requestScan' }),
    [],
  );

  return (
    <TaskProcessorProviderWrapper
      loadManifestFiles={loadManifestFiles}
      requestFileSync={requestFileSync}
    >
      {children}
    </TaskProcessorProviderWrapper>
  );
};
